/**
 * `<TypedConfirmDialog>` — confirm modal where the user must type a
 * specific phrase before the confirm button enables.
 *
 * Per NFR-Design § 2 and NFR-SEC-16. Used by:
 *  - `DeleteContactDialog` (phrase = user_id)
 *  - `RotateSigningKeyDialog` (phrase = `rotate <workspace-slug>`)
 *  - `RetireSigningKeyDialog` (phrase = `kid`)
 *
 * Wraps the existing `<Dialog>` primitive — no new design-system surface.
 * Focus management + Esc-to-cancel ship with Radix's Dialog.
 */

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@cometchat-ai/ui-dashboard";
import { useEffect, useId, useState, type ReactNode } from "react";

export interface TypedConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body: ReactNode;
  /** The exact string the user must type to enable Confirm. */
  confirmationPhrase: string;
  /** Aria/visible label on the confirmation input (interpolation done by caller). */
  inputLabel: string;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  /** Optional extra body content (e.g. an algorithm selector) above the typed input. */
  extraContent?: ReactNode;
  /** Optional test id passed through to the dialog content. */
  testId?: string;
}

export function TypedConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmationPhrase,
  inputLabel,
  confirmLabel,
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  onConfirm,
  extraContent,
  testId,
}: TypedConfirmDialogProps) {
  const inputId = useId();
  const [typed, setTyped] = useState("");

  // Reset the typed value whenever the dialog is closed; reopening should
  // not preserve a stale value from the previous interaction.
  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  const matches = typed === confirmationPhrase;
  const confirmDisabled = !matches || busy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="text-base">{title}</DialogTitle>
          <DialogDescription asChild className="text-[13px]">
            <div>{body}</div>
          </DialogDescription>
        </DialogHeader>
        {extraContent}
        <div className="space-y-2">
          <Label htmlFor={inputId} className="text-[13px]">
            {inputLabel}
          </Label>
          <Input
            id={inputId}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            autoFocus
            data-testid={testId ? `${testId}-input` : undefined}
            aria-required="true"
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => onOpenChange(false)}
            data-testid={testId ? `${testId}-cancel` : undefined}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            size="sm"
            disabled={confirmDisabled}
            onClick={onConfirm}
            data-testid={testId ? `${testId}-confirm` : undefined}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
